<template>
	<div
		ref="conveyTableRef"
		class="convey-table d-flex flex-column"
	>
		<div
			ref="conveyTableControlsRef"
			class="convey-table--controls mb-2"
		>
			<div class="row d-flex align-items-center">
				<div class="col-12 col-xl-4 py-1">
					<div class="w-100">
						<b-input-group
							v-if="showSearch"
							class="search-input"
							:class="isDarkMode ? 'dark' : ''"
						>
							<b-input-group-prepend is-text>
								<b-icon icon="search"></b-icon>
							</b-input-group-prepend>
							<b-form-input
								:placeholder="placeholders['search']"
								v-model="searchKey"
								@keyup.enter="handleSearch"
							></b-form-input>
						</b-input-group>
					</div>
				</div>
				<div class="col-12 col-xl-8 d-flex flex-wrap align-items-center py-1 gap-2">
					<div class="d-flex flex-grow-0 flex-shrink-0 flex-xl-grow-1 flex-xl-shrink-1 align-items-center justify-content-start justify-content-xl-end">
						<template v-if="showDateRange">
							<DatePicker
								:currentDateRange="dateRange"
								:isDarkMode="isDarkMode"
								:placeholder="placeholders['daterange']"
								@changeDatePicker="handleChangeDatePicker"
							/>
						</template>
					</div>
					<div
						v-if="showCreate || showExport || showFilter"
						class="d-flex align-items-center flex-nowrap gap-2"
					>
						<template v-if="showCreate">
							<slot name="create">
								<b-button
									variant="light"
									pill
									class="create-button"
									:class="isDarkMode ? 'dark' : 'light'"
									@click="handleCreate"
								>
									<slot name="create-icon">
										<b-icon icon="plus-square"></b-icon>
									</slot>
									<slot name="create-label">
										Add
									</slot>
								</b-button>
							</slot>
						</template>
						<template v-if="showExport">
							<slot name="export">
								<b-button
									variant="light"
									pill
									class="export-button"
									:class="isDarkMode ? 'dark' : 'light'"
									@click="handleExport"
								>
									<b-icon icon="upload" v-if="!isExportProcessing"></b-icon>
									<span v-if="!isExportProcessing">Export</span>

									<b-spinner
										v-if="isExportProcessing"
										variant="primary"
										label="Spinning"
										small
									></b-spinner>
									<span v-if="isExportProcessing" :style="{ opacity: 0.6 }">
										Loading
									</span>
								</b-button>
							</slot>
						</template>
						<template v-if="showFilter">
							<slot name="filter">
								<FilterDropdown
									:isDarkMode="isDarkMode"
									:filters="filterFields"
									:noClear="!enableClearAll"
									@clear="handleClearFilter"
								>
									<template
										v-for="filterField in filterFields"
										v-slot:[filterField.name]
									>
										<v-select
											:value="selectedFilters[filterField.name]"
											:key="filterField.name"
											:options="filterOptions[filterField.name]"
											:clearable="enableClear"
											@input="(e) => handleFilterChange(filterField.name, e)"
										>
											<template #search="{ attributes, events }">
												<input
													class="vs__search"
													v-bind="attributes"
													v-on="events"
												/>
											</template>
										</v-select>
									</template>
								</FilterDropdown>
							</slot>
						</template>
					</div>
				</div>
			</div>
		</div>
		<div
			ref="conveyTableContentRef"
			class="convey-table--content bg-white-custom border"
		>
			<b-overlay :show="isLoading" rounded="sm">
				<b-table
					class="convey-custom-table"
					sticky-header
					responsive
					:items="items"
					:fields="fields"
					:class="`${isDarkMode ? 'dark' : 'light'} ${tableClassName}`"
					:show-empty="totalRows === 0"
					:sort-by="sortBy"
					:sort-desc="sortDesc"
					:style="{
						'max-height': `${maxTableHeight}px`,
					}"
					@sort-changed="handleSortChanged"
				>
					<template #empty>
						<div
							class="d-flex align-items-center justify-content-center px-2 py-2"
							:style="{
								height: `${maxTableHeight - 50}px`
							}"
						>
							{{ noItemsLabel }}
						</div>
					</template>
					<template v-for="(_, slotName) of $scopedSlots" v-slot:[slotName]="scope">
						<slot :name="slotName" v-bind="scope"></slot>
					</template>
				</b-table>
			</b-overlay>
		</div>
		<div
			ref="conveyTablePaginationRef"
			class="convey-table--pagination d-flex flex-wrap justify-content-between align-items-center px-2 py-1 bg-white-custom border border-top-0 gap-2"
		>
			<span class="pagination-count">
				{{ tablePaginationLabel }}
			</span>
			<div v-if="!noPagination" class="d-flex align-items-center flex-wrap gap-2">
				<b-dropdown
					:text="`${perPage}`"
					size="sm"
					split
					:disabled="isLoading"
					class="page-size-dropdown ml-1"
					:split-class="`pagination-picker-split ${isDarkMode ? 'dark' : ''}`"
					:toggle-class="`pagination-picker-toggle ${isDarkMode ? 'dark' : ''}`"
				>
					<b-dropdown-item-button
						v-for="option in perPageOptions"
						:key="`per-page-${option}`"
						@click="selectPageSize(option)"
						:variant="option === perPage ? 'info' : ''"
					>
						{{ `${option} items / page` }}
					</b-dropdown-item-button>
				</b-dropdown>
				<b-pagination
					v-if="totalRows > 0"
					v-model="pageNumber"
					:total-rows="totalRows"
					:per-page="perPage"
					:class="isDarkMode ? 'dark' : 'light'"
					@change="handleChangePageNumber"
				></b-pagination>
			</div>
		</div>
	</div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import moment from "moment";
import vSelect from "vue-select";
import FilterDropdown from "@/components/FilterDropdown";
import DatePicker from "@/components/common/DatePicker";
import tableMixin from "@/mixins/tableMixin";

export default {
	name: "ConveyTable",
  mixins: [tableMixin],
	components: {
		vSelect,
		FilterDropdown,
		DatePicker,
	},
	props: {
		tableClassName: {
			type: String,
			default: "",
		},
		showSearch: {
			type: Boolean,
			default: false,
		},
		showFilter: {
			type: Boolean,
			default: false,
		},
		showExport: {
			type: Boolean,
			default: false,
		},
		showDateRange: {
			type: Boolean,
			default: false,
		},
		showCreate: {
			type: Boolean,
			default: false,
		},
		noPagination: {
			type: Boolean,
			default: false,
		},
		items: {
			type: Array,
			default: () => [],
		},
		fields: {
			type: Array,
			default: () => [],
		},
		placeholders: {
			type: Object,
			default: () => ({
				search: 'Search',
			}),
		},
		filterFields: {
			type: Array,
			default: () => [],
		},
		defaultFilters: {
			type: Object,
			default: () => ({}),
		},
		filterOptions: {
			type: Object,
			default: () => ({}),
		},
		enableClear: {
			type: Boolean,
			default: true,
		},
		enableClearAll: {
			type: Boolean,
			default: true,
		},
		noItemsLabel: {
			type: String,
			default: "There are no records to show"
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		isExportProcessing: {
			type: Boolean,
			default: false,
		},
		currentPage: {
			type: Number,
			default: 1,
		},
		totalRows: {
			type: Number,
			default: 0,
		},
		sortBy: {
			type: String,
			default: null,
		},
		sortDesc: {
			type: Boolean,
			default: false,
		},
		searchMode: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			maxTableHeight: 300,
			dateRange: null,
			searchKey: "",
			selectedFilters: {},
			pageNumber: 1,
			perPage: 10,
			perPageOptions: [5, 10, 25, 50, 100],
			sortByField: "",
			sortDescValue: false,
		};
	},
	computed: {
    ...mapGetters("user", [
      "currentToken",
      "viewport",
      "isMobileDevice",
      "isDarkMode",
    ]),
    ...mapGetters("agency", ["currentAgencyId"]),
    totalPages() {
      if (this.totalRows > 0) {
        if (this.totalRows % this.perPage == 0) {
          return this.totalRows / this.perPage;
        } else {
          return Math.ceil(this.totalRows / this.perPage);
        }
      } else {
        return 0;
      }
    },
    tablePaginationLabel() {
			if (this.noPagination) {
				return `Showing ${this.items.length} result${this.items.length > 1 ? 's' : ''}`;
			} else {
				const from = (this.currentPage - 1) * this.perPage + 1;
				const to = this.totalPages <= 1
					? this.items.length
					: (
						this.currentPage * this.perPage >= this.totalRows
							? this.totalRows
							: this.currentPage * this.perPage
					);
				return `Showing ${from} - ${to} from ${this.totalRows}`;
			}
    }
	},
	created() {
		this.$nextTick(() => {
			const storedTableSettings = this.storedTableSettings(this.tableClassName);
			if (storedTableSettings) {
				const {
					searchKey,
					originSearchKey,
					startDate,
					endDate,
					perPage,
					currentPage,
					sortBy,
					sortDesc,
					filter
				} = storedTableSettings;
				this.perPage = perPage;
				this.pageNumber = currentPage;
				if (this.showSearch) {
					this.searchKey = originSearchKey;
				}
				if (this.showDateRange) {
					this.dateRange = {
						start_date: startDate,
						end_date: endDate,
					};
				}
				if (this.sortBy) {
					this.sortByField = sortBy;
					this.sortDescValue = sortDesc;
				}
				if (this.showFilter) {
					this.selectedFilters = this.getSelectedFiltersFromFilterList(filter);
				}
				this.fetchItems(storedTableSettings);
			} else {
				this.perPage = 10;
				this.pageNumber = 1;
				const options = {
					perPage: this.perPage,
					currentPage: 1,
				};
				if (this.showSearch) {
					this.searchKey = "";
					options['searchKey'] = this.searchFormatter(this.searchKey);
					options['originSearchKey'] = this.searchKey;
				}
				if (this.showDateRange) {
					this.dateRange = {
						start_date: moment().startOf("year").format("YYYY-MM-DD"),
						end_date: moment().endOf("year").format("YYYY-MM-DD"),
					};
					const dateRange = this.getDateRange(this.dateRange);
					const { startDate, endDate } = dateRange;
					options['startDate'] = startDate;
					options['endDate'] = endDate;
				}
				if (this.sortBy) {
					this.sortByField = this.sortBy;
					this.sortDescValue = this.sortDesc;
					options['sortBy'] = this.sortByField;
					options['sortDesc'] = this.sortDescValue;
				}
				if (this.showFilter) {
					this.selectedFilters = {...this.defaultFilters};
					options['filter'] = this.getFilterList(this.selectedFilters);
				}
				this.fetchItems(options);
			}
		});
	},
	methods: {
    validPhoneSearch(search) {
      let searchKey = "";
      if (/^\(\d{3}\) \d{3}-\d{4}$/.test(search) === true) {
        searchKey = search.replace(/[()-\s]/g, "");
      } else {
        searchKey = search;
      }
      return searchKey;
    },
		searchFormatter(search) {
			if (!this.searchMode) {
				return search;
			} else if (this[this.searchMode]) {
				const searchKey = this[this.searchMode](search);
				return searchKey;
			} else {
				return search;
			}
		},
		getFilterList(selectedFilters) {
			const formattedFilters = [];
			if (this.filterFields.length > 0) {
				this.filterFields.forEach(filterField => {
					if (selectedFilters[filterField.name]) {
						formattedFilters.push({
							key: filterField.name,
							value: selectedFilters[filterField.name].value,
						});
					}
				});
			}
			return formattedFilters;
		},
		getSelectedFiltersFromFilterList(filters) {
			const selectedFilters = {};
			if (filters && filters.length > 0) {
				filters.forEach(filter => {
					const { key, value } = filter;
					const options = this.filterOptions[key];
					if (options) {
						const selectedOption = options.find(option => option.value === value);
						if (selectedOption) {
							selectedFilters[key] = selectedOption;
						}
					}
				});
			}
			return selectedFilters;
		},
    getDateRange(dateRange) {
      let startDate = moment().startOf("year").format("YYYY-MM-DD");
      let endDate = moment().endOf("year").format("YYYY-MM-DD");

      if (dateRange && dateRange.start_date) {
        startDate = dateRange.start_date;
        endDate = dateRange.end_date;
      }

      return { startDate, endDate };
    },
		handleCreate() {
			this.$emit("onCreate");
		},
		handleClearFilter() {
			this.selectedFilters = {};
			this.pageNumber = 1;
			const options = {
				filter: this.getFilterList(this.selectedFilters),
				currentPage: 1,
				perPage: this.perPage,
			};
			if (this.showSearch) {
				options['searchKey'] = this.searchFormatter(this.searchKey);
				options['originSearchKey'] = this.searchKey;
			}
			if (this.sortByField) {
				options['sortBy'] = this.sortByField;
				options['sortDesc'] = this.sortDescValue;
			}
			if (this.showDateRange) {
				const dateRange = this.getDateRange(this.dateRange);
				const { startDate, endDate } = dateRange;
				options['startDate'] = startDate;
				options['endDate'] = endDate;
			}
			this.fetchItems(options);
		},
		handleExport() {
			const options = {};
			if (this.showSearch) {
				options['searchKey'] = this.searchFormatter(this.searchKey);
				options['originSearchKey'] = this.searchKey;
			}
			if (this.sortByField) {
				options['sortBy'] = this.sortByField;
				options['sortDesc'] = this.sortDescValue;
			}
			if (this.showDateRange) {
				const dateRange = this.getDateRange(this.dateRange);
				const { startDate, endDate } = dateRange;
				options['startDate'] = startDate;
				options['endDate'] = endDate;
			}
			if (this.showFilter) {
				options['filter'] = this.getFilterList(this.selectedFilters);
			}
			this.$emit("onExport", options);
		},
		handleSearch() {
			const options = {
				searchKey: this.searchFormatter(this.searchKey),
				originSearchKey: this.searchKey,
				currentPage: 1,
				perPage: this.perPage,
			};
			this.pageNumber = 1;
			if (this.sortByField) {
				options['sortBy'] = this.sortByField;
				options['sortDesc'] = this.sortDescValue;
			}
			if (this.showDateRange) {
				const dateRange = this.getDateRange(this.dateRange);
				const { startDate, endDate } = dateRange;
				options['startDate'] = startDate;
				options['endDate'] = endDate;
			}
			if (this.showFilter) {
				options['filter'] = this.getFilterList(this.selectedFilters);
			}
			this.fetchItems(options);
		},
		handleSortChanged({ sortBy, sortDesc }) {
      if (sortBy && sortBy === this.sortByField) {
				this.sortDescValue = sortDesc;
				this.sortByField = sortBy;
				this.pageNumber = 1;
        const options = {
					sortBy,
					sortDesc,
					perPage: this.perPage,
					currentPage: 1,
				};
				if (this.showSearch) {
					options['searchKey'] = this.searchFormatter(this.searchKey);
					options['originSearchKey'] = this.searchKey;
				}
				if (this.showDateRange) {
					const dateRange = this.getDateRange(this.dateRange);
					const { startDate, endDate } = dateRange;
					options['startDate'] = startDate;
					options['endDate'] = endDate;
				}
				if (this.showFilter) {
					options['filter'] = this.getFilterList(this.selectedFilters);
				}
				this.fetchItems(options);
      }
		},
		handleChangeDatePicker(data) {
      if (data.start && data.end) {
        this.dateRange = {
          start_date: data.start,
          end_date: data.end,
        };
      } else {
        this.dateRange = null;
      }
			const dateRange = this.getDateRange(this.dateRange);
			const { startDate, endDate } = dateRange;
			this.pageNumber = 1;
			const options = {
				perPage: this.perPage,
				currentPage: 1,
				startDate,
				endDate,
			};
			if (this.showSearch) {
				options['searchKey'] = this.searchFormatter(this.searchKey);
				options['originSearchKey'] = this.searchKey;
			}
			if (this.sortByField) {
				options['sortBy'] = this.sortByField;
				options['sortDesc'] = this.sortDescValue;
			}
			if (this.showFilter) {
				options['filter'] = this.getFilterList(this.selectedFilters);
			}
			this.fetchItems(options);
		},
		adjustTableHeight() {
      if (
        this.$refs.conveyTableRef &&
        this.$refs.conveyTableRef.clientHeight &&
        this.$refs.conveyTableControlsRef &&
        this.$refs.conveyTableControlsRef.clientHeight &&
        this.$refs.conveyTablePaginationRef &&
        this.$refs.conveyTablePaginationRef.clientHeight
      ) {
        this.maxTableHeight = this.$refs.conveyTableRef.clientHeight
        this.maxTableHeight -= this.$refs.conveyTableControlsRef.clientHeight;
        this.maxTableHeight -= this.$refs.conveyTablePaginationRef.clientHeight;
        this.maxTableHeight -= 16;
      }
		},
		selectPageSize(option) {
      if (this.perPage !== option) {
				this.pageNumber = 1;
				this.perPage = option;
				const options = {
					perPage: option,
					currentPage: 1,
				};
				if (this.showSearch) {
					options['searchKey'] = this.searchFormatter(this.searchKey);
					options['originSearchKey'] = this.searchKey;
				}
				if (this.sortByField) {
					options['sortBy'] = this.sortByField;
					options['sortDesc'] = this.sortDescValue;
				}
				if (this.showFilter) {
					options['filter'] = this.getFilterList(this.selectedFilters);
				}
				if (this.showDateRange) {
					const dateRange = this.getDateRange(this.dateRange);
					const { startDate, endDate } = dateRange;
					options['startDate'] = startDate;
					options['endDate'] = endDate;
				}
				this.fetchItems(options);
      }
		},
		fetchItems(options) {
			this.storeTableSettings(this.tableClassName, options);
			this.$emit("onFetch", options);
		},
		handleFilterChange(filterName, e) {
			if (e) {
				this.selectedFilters[filterName] = {...e};
			} else {
				delete this.selectedFilters[filterName];
			}
			this.pageNumber = 1;
			const options = {
				filter: this.getFilterList(this.selectedFilters),
				currentPage: 1,
				perPage: this.perPage,
			};
			if (this.showSearch) {
				options['searchKey'] = this.searchFormatter(this.searchKey);
				options['originSearchKey'] = this.searchKey;
			}
			if (this.sortByField) {
				options['sortBy'] = this.sortByField;
				options['sortDesc'] = this.sortDescValue;
			}
			if (this.showDateRange) {
				const dateRange = this.getDateRange(this.dateRange);
				const { startDate, endDate } = dateRange;
				options['startDate'] = startDate;
				options['endDate'] = endDate;
			}
			this.fetchItems(options);
		},
		handleChangePageNumber(e) {
			this.pageNumber = e;
			const options = {
				currentPage: e,
				perPage: this.perPage,
			};
			if (this.showSearch) {
				options['searchKey'] = this.searchFormatter(this.searchKey);
				options['originSearchKey'] = this.searchKey;
			}
			if (this.sortByField) {
				options['sortBy'] = this.sortByField;
				options['sortDesc'] = this.sortDescValue;
			}
			if (this.showDateRange) {
				const dateRange = this.getDateRange(this.dateRange);
				const { startDate, endDate } = dateRange;
				options['startDate'] = startDate;
				options['endDate'] = endDate;
			}
			if (this.showFilter) {
				options['filter'] = this.getFilterList(this.selectedFilters);
			}
			this.fetchItems(options);
		}
	},
	watch: {
		isLoading(newVal, oldVal) {
			if (oldVal === true && newVal !== oldVal) {
				this.$nextTick(() => {
					this.adjustTableHeight();
				});
			}
		},
    viewport: {
      handler() {
				this.$nextTick(() => {
					this.adjustTableHeight();
				});
      },
      deep: true,
      immediate: true,
    },
	},
};
</script>

<style lang="scss">
.convey-table {
	width: 100%;
	height: 100%;
	
	&--controls {
		.search-input {
			width: 100% !important;
			border-radius: 2rem;
			.input-group-prepend {
				.input-group-text {
					border-radius: 2rem 0 0 2rem !important;
				}
			}
			.form-control {
				font-size: 0.875rem;
			}
			input {
				border-radius: 0 2rem 2rem 0 !important;
			}
			&.dark {
				input::placeholder {
					color: #b2b9bf !important;
				}
				&::before {
					color: #b2b9bf !important;
				}
			}
		}

		.create-button,
		.export-button {
			color: #5c59e8;
			background: #dedefa;
			font-size: 0.875rem;
			display: flex;
			flex-wrap: nowrap;
			align-items: center;
			gap: 8px;
			&.dark {
				color: #fff;
				background-color: #263238;
				border-color: rgba(255, 255, 255, 0.2) !important;
			}
		}
	}
	&--content {
		flex: 1;
		border-radius: 1rem 1rem 0 0;

		>div.b-overlay-wrap {
			height: 100%;
			border-radius: 1rem 1rem 0 0;
		}

		.convey-custom-table {
			margin: 0 !important;
			height: 100%;
			border-radius: 1rem 1rem 0 0;

			.custom-column {
				min-width: unset !important;
			}
			&.dark {
				thead > tr > th {
					background-color: #44475b !important;
					border-top: none;
					border-bottom: 1px solid #f0f1f3;
					color: #b2b9bf !important;
				}
				tbody > tr > td {
					color: #b2b9bf !important;
				}
			}
			thead > tr {
				border-radius: 1rem 1rem 0 0;
			}
			thead > tr > th {
				position: sticky !important;
				background-color: #f9f9fc !important;
				border-top: none;
				border-bottom: 1px solid #f0f1f3;
				color: #333843 !important;
				font-size: 0.875rem;
				font-weight: 500;
				text-align: center;
			}
			tbody > tr > td {
				color: #667085 !important;
				font-size: 0.875rem;
				font-weight: 400;
				vertical-align: middle;
				text-align: center;
				min-width: 150px;
			}
		}
	}
	&--pagination {
		border-radius: 0 0 1rem 1rem;
		.pagination-count {
			font-size: 0.875rem;
			color: #667085;
			font-weight: 500;
		}

		.pagination-picker-split {
			border-radius: 8px 0 0 8px !important;
			font-size: 0.875rem;
			color: #fff !important;
			border-color: #186dde !important;
			background-color: #186dde !important;
			&.dark {
				background-color: #186dde !important;
				color: #b2b9bf !important;
				border-color: rgba(255, 255, 255, 0.2) !important;
			}
		}

		.pagination-picker-toggle {
			border-radius: 0 8px 8px 0 !important;
			color: #fff !important;
			background-color: #186dde !important;
			border-color: #186dde !important;
			&.dark {
				background-color: #186dde !important;
				color: #b2b9bf !important;
				border-color: rgba(255, 255, 255, 0.2) !important;
			}
		}

		.pagination {
			justify-content: end;
			margin-bottom: 0;
			&.dark {
				.page-item .page-link {
					color: #b2b9bf !important;
				}
			}
			.page-item {
				padding: 4px;

				&.active {
					.page-link {
						color: #fff;
						background-color: #186dde !important;
					}
				}

				.page-link {
					border-radius: 8px;
					color: #186dde;
					background: #dedefa;
					font-size: 0.875rem;
					font-weight: 600;
				}
			}
		}
	}
}
</style>